'use client';

import PropTypes from 'prop-types';
import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@helpers/styleUtils';
import { buttonVariants } from './Button';

function Calendar({
    className,
    selected,
    onSelect,
    mode = 'single',
    numberOfMonths = 1,
    components,
}) {
    const [currentDate, setCurrentDate] = React.useState(new Date());

    const daysInMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0,
    ).getDate();

    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

    const previousMonth = () => {
        setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
    };

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    const renderCalendarGrid = currentDateToRender => (
        <div className="grid grid-cols-7 gap-1">
            {days.map(day => (
                <div
                    key={day}
                    className="text-muted-foreground text-center text-[0.8rem] font-normal"
                >
                    {day}
                </div>
            ))}

            {Array.from({ length: firstDayOfMonth }).map((_, index) => (
                <div key={`empty-${index}`} />
            ))}

            {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const date = new Date(
                    currentDateToRender.getFullYear(),
                    currentDateToRender.getMonth(),
                    day,
                );
                const isSelected =
                    mode === 'range'
                        ? selected?.from &&
                          selected?.to &&
                          date >= selected.from &&
                          date <= selected.to
                        : selected?.toDateString() === date.toDateString();
                const isToday = new Date().toDateString() === date.toDateString();

                const dayContent = components?.DayContent ? components.DayContent(date) : day;

                return (
                    <button
                        key={day}
                        onClick={() => {
                            if (mode === 'range') {
                                if (!selected?.from || (selected.from && selected.to)) {
                                    onSelect?.({ from: date, to: undefined });
                                } else {
                                    onSelect?.({ ...selected, to: date });
                                }
                            } else {
                                onSelect?.(date);
                            }
                        }}
                        className={cn(
                            buttonVariants({ variant: 'ghost' }),
                            'h-8 w-8 p-0 font-normal rounded-full',
                            isSelected &&
                                'bg-primary-blue/60 text-white hover:bg-primary-blue/50 hover:text-white',
                            isToday &&
                                !isSelected &&
                                'bg-primary-blue text-white hover:bg-primary-blue/50 hover:text-white',
                        )}
                    >
                        {dayContent}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className={cn('p-3 flex gap-4', className)}>
            {Array.from({ length: numberOfMonths }).map((_, index) => {
                const monthDate = new Date(currentDate);
                monthDate.setMonth(monthDate.getMonth() + index);

                return (
                    <div key={index} className="w-full">
                        <div className="flex justify-between items-center mb-4">
                            {index === 0 && (
                                <button
                                    onClick={previousMonth}
                                    className={cn(
                                        buttonVariants({ variant: 'outline' }),
                                        'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                                    )}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                            )}
                            <div className="text-sm font-medium">
                                {months[monthDate.getMonth()]} {monthDate.getFullYear()}
                            </div>
                            {index === numberOfMonths - 1 && (
                                <button
                                    onClick={nextMonth}
                                    className={cn(
                                        buttonVariants({ variant: 'outline' }),
                                        'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                                    )}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        {renderCalendarGrid(monthDate)}
                    </div>
                );
            })}
        </div>
    );
}

Calendar.propTypes = {
    className: PropTypes.string,
    selected: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.shape({
            from: PropTypes.instanceOf(Date),
            to: PropTypes.instanceOf(Date),
        }),
    ]),
    onSelect: PropTypes.func,
    mode: PropTypes.oneOf(['single', 'range']),
    numberOfMonths: PropTypes.number,
    components: PropTypes.shape({
        DayContent: PropTypes.func,
    }),
};

export { Calendar };
